import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { parseEgressConfig } from '@nadoutong/contracts';
import type {
  EgressAdmissionBlacklist,
  EgressConfig,
  EgressFirewallRef,
  EgressProfileRecord,
  KeyChannelBinding,
  KeyChannelBindingCascadeImpact,
  SetKeyChannelBindingsRequest,
  UpstreamChannelRecord,
} from '@nadoutong/contracts';
import { useEgressProfiles } from '../api/useEgressProfiles.ts';
import { useFirewallRulesets } from '../api/useFirewallRulesets.ts';
import { useSetKeyChannelBindings } from '../api/useKeyChannelBindings.ts';
import { cascadeImpactFromError } from '../api/keyChannelBindings.ts';
import { useUpstreamChannels } from '../api/useUpstreams.ts';
import { Cable, Eraser, Link2, Plus, Route, Save, TriangleAlert, Unlink, X } from 'lucide-react';
import { useT } from '../i18n/index.ts';
import { Button, Dialog, Select } from '../ui/index.ts';
import {
  armedBlacklistRules,
  bindingBlacklistState,
} from '../lib/egressAdmissionBlacklist.ts';
import {
  bindingFirewallState,
  composeBindingEgressOverride,
  profileWithBindingFirewall,
  selectableFirewallRulesets,
} from '../lib/egressBindingFirewall.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';
import { Modal } from './Modal.tsx';
import { EgressAdmissionBlacklistFields } from './EgressAdmissionBlacklistFields.tsx';
import { EgressFirewallFields } from './EgressFirewallFields.tsx';
import { egressConfigSummaryLabel } from './EgressConfigEditor.tsx';

/**
 * A stored per-binding override, as this sheet carries it.
 *
 * Both forms are kept because they serve different jobs: `config` is what a
 * save resubmits, `json` is what the operator reads. Deriving the text from the
 * parsed value would show a document that is subtly not the stored one.
 *
 * `config: null` means the stored text is not a full document the write face
 * accepts — a legacy partial fragment, which `resolveEgressConfig` still honours
 * at runtime by deep-merging it over the profile. Such a value is live but
 * unexpressible on the way back in, so a save clears it and the sheet says which
 * channel that happens to, by name, before the button is pressed.
 */
type DraftOverride = {
  readonly json: string;
  readonly config: EgressConfig | null;
};

type DraftChannelBinding = {
  readonly channel_id: string;
  readonly channel_slug: string;
  readonly egress_profile_id: string | null;
  /**
   * Typed per-binding override document, read-only here but **carried through
   * a save**. This endpoint replaces the bucket's entire binding set, so a
   * resubmission that omits it deletes it — and it deletes it on every binding
   * in the bucket, not just the one being edited (#1536).
   *
   * Since #1609 one part of it is editable: the `require-cc-black` rule set,
   * composed onto the selected profile's own document by
   * `composeBindingBlacklistOverride`. Everything else in a stored override is
   * still read-only and still carried, and an override this editor cannot
   * reproduce from the profile is left strictly alone (`bindingBlacklistState`
   * reports `diverged`).
   *
   * Four things clear one. The operator arming and then unarming every rule,
   * since an override that only renames the admission mode pins a profile
   * snapshot for no benefit. Selecting the default profile, since the write
   * face requires a profile for an override to apply over. Asking for it
   * directly (#1615), which is the only one of the four that keeps the profile
   * bound and is therefore the only one that does not pass through a stored
   * state with no profile at all. And the unavoidable one: a stored value the
   * write face cannot accept back (see `DraftOverride`), which goes away on
   * *any* save of this bucket even when nothing was edited — that last path is
   * why it takes an explicit confirmation, not a footer label.
   */
  readonly override: DraftOverride | null;
};

/**
 * A save this sheet holds behind a confirmation, and why.
 *
 * Both arms end a live egress policy, which is why they share one dialog. They
 * are separate cases because only one of them is the operator's own intent:
 * `unsupported` is a stored fragment the write face cannot express, which any
 * save of this bucket removes whether or not anything was edited; `clear` is
 * the operator asking for exactly this on one named binding (#1615).
 */
type SaveConfirm =
  | { readonly kind: 'unsupported'; readonly channels: readonly string[] }
  | {
    readonly kind: 'clear';
    readonly channelId: string;
    readonly channel: string;
    /** Fragment overrides on other bindings this same save still clears. */
    readonly alsoCleared: readonly string[];
  };

function draftFromBindings(bindings: readonly KeyChannelBinding[]): DraftChannelBinding[] {
  return bindings.map((binding) => ({
    channel_id: binding.channel_id,
    channel_slug: binding.channel_slug,
    egress_profile_id: binding.egress_profile_id,
    override: draftOverride(binding.egress_config_override_json),
  }));
}

/**
 * A stored reference as one line, for the read-only branch.
 *
 * Ruleset id rather than name: this renders in the state where the document
 * cannot be reproduced, so it says what is actually written down rather than
 * resolving it against a list that may not contain it.
 */
function firewallRefLabel(ref: EgressFirewallRef): string {
  return ref.version === undefined ? ref.rulesetId : `${ref.rulesetId} @ v${ref.version}`;
}

function draftOverride(text: string | null): DraftOverride | null {
  if (text === null || text.trim().length === 0 || text.trim() === '{}') {
    return null;
  }
  return { json: text, config: parseStoredOverride(text) };
}

/**
 * Stored override text as a typed document, or null when it cannot be read.
 *
 * The gateway only ever stores `serializeEgressConfig(parseEgressConfig(...))`,
 * so this parses in practice. It is written to tolerate the case anyway,
 * because the alternative to noticing an unreadable document is resubmitting
 * without it, which is precisely the deletion this change exists to stop.
 */
function parseStoredOverride(text: string): EgressConfig | null {
  try {
    return parseEgressConfig(JSON.parse(text));
  } catch {
    return null;
  }
}

function draftFingerprint(bindings: readonly DraftChannelBinding[]): string {
  return JSON.stringify(
    [...bindings]
      .map((binding) => ({
        channel_id: binding.channel_id,
        channel_slug: binding.channel_slug,
        egress_profile_id: binding.egress_profile_id,
        // Included so that clearing an override — the one edit here that can
        // destroy stored configuration — registers as an unsaved change
        // instead of leaving the sheet looking untouched.
        override: binding.override?.json ?? null,
      }))
      .sort((a, b) => a.channel_id.localeCompare(b.channel_id)),
  );
}

/**
 * Radix Select items cannot use an empty-string value; the "default"
 * (no profile) choice travels as this sentinel and maps back to null.
 */
const DEFAULT_PROFILE_SENTINEL = '__default__';

function isChannelInstanceProfile(profile: EgressProfileRecord): boolean {
  return profile.id.startsWith('egp_global_');
}

function channelDisplayName(binding: DraftChannelBinding): string {
  return binding.channel_slug;
}

export function KeyChannelEgressSheet({
  upstreamId,
  bucketId,
  bucketLabel,
  currentBindings,
  initialAddChannelId = null,
  onClose,
}: {
  readonly upstreamId: string;
  readonly bucketId: string;
  readonly bucketLabel: string;
  readonly currentBindings: readonly KeyChannelBinding[];
  readonly initialAddChannelId?: string | null;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const profilesQ = useEgressProfiles();
  const channelsQ = useUpstreamChannels(upstreamId);
  // Only requested by the surface that offers the selector, as on the profile
  // side. A list that fails to load does not hide a stored reference — see
  // `rulesetsUnavailable` below.
  const firewallRulesetsQ = useFirewallRulesets({ scope: 'shared' });
  // Binding-scoped rulesets are a separate list — the endpoint filters by one
  // scope at a time. Fetched for the whole bucket rather than per channel so
  // switching channels inside the sheet does not refetch; the per-channel
  // narrowing happens below, where the selected channel is known.
  const ownedRulesetsQ = useFirewallRulesets({
    scope: 'binding',
    owner_key_bucket_id: bucketId,
  });
  const setMut = useSetKeyChannelBindings(upstreamId, bucketId);
  const toast = useToast();
  const profiles = (profilesQ.data ?? []).filter((profile) => !isChannelInstanceProfile(profile));
  const upstreamChannels = (channelsQ.data ?? []).filter((channel) => channel.enabled);
  const [draftBindings, setDraftBindings] = useState<DraftChannelBinding[]>(
    () => draftFromBindings(currentBindings),
  );
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    () => currentBindings[0]?.channel_id ?? null,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** The pending "this save clears a live override" confirmation, if any. */
  const [saveConfirm, setSaveConfirm] = useState<SaveConfirm | null>(null);
  const [cascadeConfirm, setCascadeConfirm] = useState<{
    readonly impact: KeyChannelBindingCascadeImpact;
    readonly request: SetKeyChannelBindingsRequest;
  } | null>(null);

  useEffect(() => {
    const next = draftFromBindings(currentBindings);
    setDraftBindings(next);
    setSelectedChannelId((current) => {
      if (
        initialAddChannelId !== null &&
        next.some((binding) => binding.channel_id === initialAddChannelId)
      ) {
        return initialAddChannelId;
      }
      if (current !== null && next.some((binding) => binding.channel_id === current)) {
        return current;
      }
      return next[0]?.channel_id ?? null;
    });
    setFormError(null);
  }, [currentBindings, initialAddChannelId]);

  const selectedBinding = selectedChannelId === null
    ? undefined
    : draftBindings.find((binding) => binding.channel_id === selectedChannelId);
  const selectedProfile = selectedBinding?.egress_profile_id === null || selectedBinding === undefined
    ? undefined
    : profiles.find((profile) => profile.id === selectedBinding.egress_profile_id);
  const selectedPlainName = selectedBinding?.channel_slug ?? '';
  const initialFingerprint = draftFingerprint(draftFromBindings(currentBindings));
  const currentFingerprint = draftFingerprint(draftBindings);
  const dirty = initialFingerprint !== currentFingerprint;
  /**
   * Channels whose stored override this save would clear — not channels that
   * have one.
   *
   * That distinction is the whole of #1536: the count used to be "every stored
   * override", because a save discarded all of them. A save now carries them,
   * so the warning must name only the ones actually going away, and it has to
   * be visible before the button is pressed rather than discovered afterwards.
   *
   * Two ways an override still goes away. The operator's own edit — removing
   * the binding, or selecting the default profile, which an override cannot
   * apply without. And a stored document the write face cannot accept back:
   * `resolveEgressConfig` also honours a partial fragment deep-merged over the
   * profile, but the write face takes full documents only, so such a value is
   * live at runtime and unexpressible on the way back in.
   */
  const clearedOverrides = useMemo((): readonly { slug: string; reason: 'edited' | 'unsupported' }[] => {
    const kept = new Map(draftBindings.map((binding) => [binding.channel_id, binding]));
    const cleared: { slug: string; reason: 'edited' | 'unsupported' }[] = [];
    for (const binding of currentBindings) {
      if (draftOverride(binding.egress_config_override_json) === null) continue;
      const draft = kept.get(binding.channel_id);
      if (draft === undefined || draft.override === null || draft.egress_profile_id === null) {
        cleared.push({ slug: binding.channel_slug, reason: 'edited' });
        continue;
      }
      if (draft.override.config === null) {
        cleared.push({ slug: binding.channel_slug, reason: 'unsupported' });
      }
    }
    return cleared;
  }, [currentBindings, draftBindings]);
  const editedAway = clearedOverrides.filter((entry) => entry.reason === 'edited').map((e) => e.slug);
  const unsupported = clearedOverrides.filter((entry) => entry.reason === 'unsupported').map((e) => e.slug);

  /**
   * Which blacklist control the selected binding gets, if any.
   *
   * Derived from the profile plus the stored override rather than from a piece
   * of local state, so a profile that changes under an existing override moves
   * the sheet into the read-only branch on the next render instead of offering
   * an edit that would overwrite the difference.
   *
   * The whole `DraftOverride` goes in, not `override.config`. Those are three
   * states, not two — no override, a full document, and a live partial
   * fragment the write face cannot accept back — and flattening the last two
   * with `?? null` is what made a fragment-bearing binding editable, so
   * ticking a rule replaced the fragment and the resulting document parsed
   * cleanly enough to skip the #1536 confirmation on the way out.
   */
  const firewallState = bindingFirewallState(
    selectedProfile?.egress_config,
    selectedBinding?.override ?? null,
  );
  const bindingFirewallRef = firewallState.kind === 'unavailable' ? null : firewallState.firewall;
  const blacklistState = bindingBlacklistState(
    // Folded so that arming ① does not take ②'s checkboxes away; see
    // `profileWithBindingFirewall`.
    profileWithBindingFirewall(selectedProfile?.egress_config, bindingFirewallRef),
    selectedBinding?.override ?? null,
  );

  /**
   * Rewrite the selected binding's override from the profile plus both
   * editable axes.
   *
   * Recomposing rather than patching the stored override is what keeps the
   * document a valid full `EgressConfig` — the only thing the write face takes
   * — and it is why the composition is a snapshot: the profile it copies is the
   * one on screen now. Both handlers below pass the OTHER axis's current value
   * through unchanged, which is what stops an edit on one from deleting the
   * other. They only ever run from an `editable` branch, where the stored
   * override is already reproducible from that same profile.
   */
  function applyBindingOverride(
    axes: {
      readonly blacklist: EgressAdmissionBlacklist | null;
      readonly firewall: EgressFirewallRef | null;
    },
    failureMessage: string,
  ): void {
    const profileConfig = selectedProfile?.egress_config;
    if (profileConfig === undefined || profileConfig === null) return;
    const composed = composeBindingEgressOverride(profileConfig, axes);
    if (!composed.ok) {
      // A composition that will not parse must not read as "clear the
      // override" — that is the deletion #1536 exists to stop.
      setFormError(failureMessage);
      return;
    }
    setFormError(null);
    updateSelectedBinding({
      override: composed.override === null
        ? null
        : { json: JSON.stringify(composed.override, null, 2), config: composed.override },
    });
  }

  function applyBlacklistRules(rules: EgressAdmissionBlacklist): void {
    applyBindingOverride(
      { blacklist: rules, firewall: bindingFirewallRef },
      t('upstream_accounts.egress.binding_blacklist_compose_failed'),
    );
  }

  function applyFirewallRef(next: EgressFirewallRef | null): void {
    applyBindingOverride(
      {
        blacklist: blacklistState.kind === 'unavailable' ? null : blacklistState.rules,
        firewall: next,
      },
      t('upstream_accounts.egress.binding_firewall_compose_failed'),
    );
  }

  function updateSelectedBinding(patch: Partial<DraftChannelBinding>): void {
    if (selectedChannelId === null) {
      return;
    }
    setDraftBindings((prev) =>
      prev.map((binding) =>
        binding.channel_id === selectedChannelId
          ? { ...binding, ...patch }
          : binding,
      ),
    );
  }

  function removeSelectedBinding(): void {
    if (selectedChannelId === null) {
      return;
    }
    setDraftBindings((prev) => {
      const next = prev.filter((binding) => binding.channel_id !== selectedChannelId);
      setSelectedChannelId(next[0]?.channel_id ?? null);
      return next;
    });
  }

  function addBinding(channel: { readonly channel_id: string; readonly channel_slug: string; readonly egress_profile_id: string | null }): void {
    const binding: DraftChannelBinding = {
      channel_id: channel.channel_id,
      channel_slug: channel.channel_slug,
      egress_profile_id: channel.egress_profile_id,
      // A newly bound channel has no stored override to carry.
      override: null,
    };
    setDraftBindings((prev) => [...prev, binding]);
    setSelectedChannelId(binding.channel_id);
    setAddOpen(false);
  }

  function handleSave(): void {
    // A fragment-only override goes away on ANY save of this bucket, including
    // one where nothing was edited — so a footer label is the wrong weight for
    // it. This file already gates destructive server-side effects behind an
    // explicit dialog (`CascadeConfirmDialog`); losing a live egress policy
    // deserves at least the same, and one extra click is neither the silent
    // deletion this change removes nor the bucket-wide lockout a refusal
    // would impose.
    if (unsupported.length > 0) {
      setSaveConfirm({ kind: 'unsupported', channels: unsupported });
      return;
    }
    submitDraft(draftBindings);
  }

  /**
   * Clear the selected binding's override, keeping its profile (#1615).
   *
   * The recovery path this replaces cost two saves: select the default
   * profile and save to drop the override, then re-bind the profile and save
   * again. Between them the binding is stored with NO profile and falls back
   * to the channel default — on a cc-lane binding carrying tens of thousands
   * of requests a day that is a tenant-visible window, not a UI inconvenience.
   *
   * It stays behind the same confirmation as the fragment path, because it is
   * the same event: a live egress policy stops applying. Deliberately NOT
   * paired with a "re-sync from profile" button — a stale snapshot and a
   * deliberate hand edit are indistinguishable from here, so recomposing would
   * sometimes silently overwrite the second.
   */
  function handleClearOverride(): void {
    if (selectedBinding === undefined || selectedBinding.override === null) return;
    setSaveConfirm({
      kind: 'clear',
      channelId: selectedBinding.channel_id,
      channel: selectedBinding.channel_slug,
      // Fragments on OTHER bindings still go away with this save — the whole
      // point of #1536 is that the operator learns that before the write, not
      // after — so the confirmation names them alongside the deliberate clear.
      alsoCleared: unsupported.filter((slug) => slug !== selectedBinding.channel_slug),
    });
  }

  function submitConfirmedSave(confirm: SaveConfirm): void {
    setSaveConfirm(null);
    if (confirm.kind === 'unsupported') {
      submitDraft(draftBindings);
      return;
    }
    // Computed rather than read back from state: `setDraftBindings` does not
    // land before this call, so submitting from state here would send the
    // override it was asked to clear.
    const next = draftBindings.map((binding) =>
      binding.channel_id === confirm.channelId ? { ...binding, override: null } : binding,
    );
    setDraftBindings(next);
    submitDraft(next);
  }

  function submitDraft(bindingsDraft: readonly DraftChannelBinding[]): void {
    const bindings = bindingsDraft.map((binding) => ({
      channel_id: binding.channel_id,
      egress_profile_id: binding.egress_profile_id,
      // Carried through untouched. Omitting it is a deletion, not a no-op:
      // this endpoint replaces the bucket's whole binding set. Only sent
      // alongside a profile, which the write face requires for an override.
      ...(binding.egress_profile_id !== null && binding.override?.config != null
        ? { egress_config_override: binding.override.config }
        : {}),
    }));

    setFormError(null);
    submitBindings({ bindings });
  }

  function submitBindings(request: SetKeyChannelBindingsRequest): void {
    setMut.mutate(request, {
      onSuccess: () => {
        setCascadeConfirm(null);
        toast.success(t('upstream_accounts.keys.toast.channels_updated'));
        onClose();
      },
      onError: (err) => {
        const impact = cascadeImpactFromError(err);
        if (impact !== null) {
          setCascadeConfirm({ impact, request });
          return;
        }
        setCascadeConfirm(null);
        toast.error(coerceErrorMessage(err));
      },
    });
  }

  function handleCascadeConfirm(): void {
    if (cascadeConfirm === null) {
      return;
    }
    submitBindings({ ...cascadeConfirm.request, confirm_cascade: true });
  }

  const boundChannelIds = useMemo(
    () => new Set(draftBindings.map((binding) => binding.channel_id)),
    [draftBindings],
  );

  useEffect(() => {
    if (
      initialAddChannelId !== null &&
      initialAddChannelId.length > 0 &&
      !boundChannelIds.has(initialAddChannelId)
    ) {
      setAddOpen(true);
    }
  }, [boundChannelIds, initialAddChannelId]);

  return (
    <>
      <Modal
        isOpen
        onClose={() => {
          if (!setMut.isPending) onClose();
        }}
        titleId="key-channel-egress-sheet-title"
        testId="key-channel-egress-sheet"
        containerClassName="items-stretch justify-end p-0"
        panelClassName="h-full max-h-screen max-w-none overflow-hidden bg-canvas sm:w-[min(100vw,1120px)]"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-line-soft bg-card px-6 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Route aria-hidden="true" className="h-5 w-5 shrink-0 text-signal" />
                <h2 id="key-channel-egress-sheet-title" className="m-0 truncate text-title text-paper">
                  {t('upstream_accounts.egress.sheet_title', { key: bucketLabel })}
                </h2>
              </div>
              <p className="mt-1 text-label text-paper-low">
                {t('upstream_accounts.egress.config_body')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={setMut.isPending}
              aria-label={t('common.close')}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-50"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden bg-canvas p-3 lg:grid-cols-[240px_minmax(0,1fr)] lg:p-4">
            <aside className="flex min-h-0 flex-col rounded-lg border border-line-soft bg-card p-3 max-lg:max-h-[220px]">
              <div className="flex items-center justify-between gap-2">
                <h3 className="m-0 text-label font-medium text-paper-mid">
                  {t('upstream_accounts.egress.bound_channels')}
                </h3>
                <span className="font-mono text-micro text-paper-low">{draftBindings.length}</span>
              </div>
              <div className="mt-3 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                {draftBindings.length === 0 ? (
                  <div className="rounded-lg bg-well px-3 py-4 text-label text-paper-low">
                    {t('upstream_accounts.egress.no_bound_channels')}
                  </div>
                ) : (
                  draftBindings.map((binding) => {
                    const profile = binding.egress_profile_id === null
                      ? undefined
                      : profiles.find((item) => item.id === binding.egress_profile_id);
                    const active = binding.channel_id === selectedChannelId;
                    return (
                      <button
                        key={binding.channel_id}
                        type="button"
                        onClick={() => setSelectedChannelId(binding.channel_id)}
                        aria-pressed={active}
                        className={`rounded px-3 py-2 text-left transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 ${
                          active
                            ? 'bg-active text-paper'
                            : 'bg-well text-paper-mid hover:bg-raised hover:text-paper'
                        }`}
                      >
                        <span className="block text-dense font-medium">
                          {channelDisplayName(binding)}
                        </span>
                        <span className="mt-1 block truncate font-mono text-micro text-paper-low">
                          {profile?.name ?? t('upstream_accounts.egress.default')}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
              <div className="mt-3 grid gap-2">
                <Button
                  size="sm"
                  onClick={() => setAddOpen(true)}
                  disabled={setMut.isPending}
                >
                  <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                  <span>{t('upstream_accounts.egress.add_channel')}</span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={removeSelectedBinding}
                  disabled={selectedBinding === undefined || setMut.isPending}
                  className="hover:bg-bad-dim hover:text-bad"
                >
                  <Unlink aria-hidden="true" className="h-3.5 w-3.5" />
                  <span>{t('upstream_accounts.egress.remove_channel')}</span>
                </Button>
              </div>
            </aside>

            <section className="min-w-0 overflow-y-auto overflow-x-hidden rounded-lg border border-line-soft bg-card p-4">
              {selectedBinding === undefined ? (
                <div className="flex min-h-[360px] items-center justify-center rounded-lg bg-well px-6 text-center text-body text-paper-low">
                  {t('upstream_accounts.egress.no_bound_channels')}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <p className="m-0 text-label text-paper-low">
                        {t('upstream_accounts.egress.selected_channel')}
                      </p>
                      <h3 className="mt-1 text-section text-paper">
                        {channelDisplayName(selectedBinding)}
                      </h3>
                      <p className="mt-1 font-mono text-label text-paper-low">
                        {selectedBinding.channel_id}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/supply/egress-profiles"
                        className="inline-flex h-7 shrink-0 items-center justify-center gap-1 rounded border border-line px-2.5 text-label font-medium text-paper-mid transition-colors duration-120 hover:border-line-strong hover:bg-card hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                      >
                        <Cable aria-hidden="true" className="h-3.5 w-3.5" />
                        <span>{t('upstream_accounts.egress.manage_templates')}</span>
                      </Link>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                    <div className="flex flex-col gap-2">
                      <span className="text-label text-paper-mid">
                        {t('upstream_accounts.egress.label')}
                      </span>
                      <Select
                        value={selectedBinding.egress_profile_id ?? DEFAULT_PROFILE_SENTINEL}
                        onValueChange={(value) => {
                          const nextProfileId = value === DEFAULT_PROFILE_SENTINEL ? null : value;
                          // An override applies over a profile document, so the
                          // write face requires one; choosing the default has
                          // to take the override with it. This previously
                          // patched `egress_options_json`, a field this draft
                          // type does not have — a no-op that TypeScript could
                          // not see because a conditional spread is exempt from
                          // excess-property checking.
                          updateSelectedBinding({
                            egress_profile_id: nextProfileId,
                            ...(nextProfileId === null ? { override: null } : {}),
                          });
                        }}
                        disabled={setMut.isPending || profilesQ.isLoading}
                        ariaLabel={`${selectedPlainName} ${t('upstream_accounts.egress.label')}`}
                        options={[
                          { value: DEFAULT_PROFILE_SENTINEL, label: t('upstream_accounts.egress.default') },
                          ...profiles.map((profile) => ({
                            value: profile.id,
                            label: `${profile.name} · ${egressConfigSummaryLabel(profile.egress_config, t)}`,
                          })),
                        ]}
                      />
                    </div>
                    <div className="rounded-lg bg-well px-3 py-2">
                      <span className="text-micro font-medium text-paper-low">
                        {t('upstream_accounts.egress.effective')}
                      </span>
                      <span className="mt-2 inline-flex rounded-full bg-signal-dim px-2 py-1 font-mono text-micro text-signal">
                        {selectedProfile === undefined
                          ? t('upstream_accounts.egress.default')
                          : egressConfigSummaryLabel(selectedProfile.egress_config, t)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg bg-well p-3">
                    <h4 className="m-0 text-label font-medium text-paper-mid">
                      {t('upstream_accounts.egress.typed_config')}
                    </h4>
                    {selectedProfile?.egress_config !== undefined && selectedProfile?.egress_config !== null ? (
                      <pre className="mt-3 max-h-56 overflow-auto rounded-lg bg-card p-3 font-mono text-label text-paper-mid">
                        {JSON.stringify(selectedProfile.egress_config, null, 2)}
                      </pre>
                    ) : (
                      <div className="mt-3 rounded-lg bg-card px-3 py-3 text-dense text-paper-low">
                        {t('upstream_accounts.egress.typed_config_empty')}
                      </div>
                    )}
                    {firewallState.kind === 'editable' ? (
                      <div
                        className="mt-3 flex flex-col gap-2"
                        data-testid="binding-firewall-editor"
                      >
                        <p className="m-0 text-label text-paper-mid">
                          {t('upstream_accounts.egress.binding_firewall_scope', {
                            channel: selectedPlainName,
                          })}
                        </p>
                        <EgressFirewallFields
                          value={firewallState.firewall}
                          rulesets={selectableFirewallRulesets(
                            firewallRulesetsQ.data,
                            ownedRulesetsQ.data,
                            selectedChannelId,
                          )}
                          rulesetsUnavailable={firewallRulesetsQ.isError || ownedRulesetsQ.isError}
                          scope="binding"
                          disabled={setMut.isPending}
                          idPrefix="binding"
                          onChange={applyFirewallRef}
                        />
                        <p className="m-0 text-label text-paper-low">
                          {t('upstream_accounts.egress.binding_firewall_snapshot')}
                        </p>
                      </div>
                    ) : null}
                    {firewallState.kind === 'diverged' ? (
                      <div
                        className="mt-3 flex flex-col gap-2 rounded-lg border border-warn/40 bg-warn-dim/40 p-3"
                        data-testid="binding-firewall-diverged"
                      >
                        <span className="inline-flex items-center gap-2 text-label font-medium text-warn">
                          <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />
                          {t('upstream_accounts.egress.binding_firewall_diverged_title')}
                        </span>
                        <p className="m-0 text-label text-paper-mid">
                          {t('upstream_accounts.egress.binding_firewall_diverged_body')}
                        </p>
                        <p className="m-0 font-mono text-micro text-paper-low">
                          {firewallState.firewall === null
                            ? t('upstream_accounts.egress.binding_firewall_none')
                            : firewallRefLabel(firewallState.firewall)}
                        </p>
                      </div>
                    ) : null}
                    {firewallState.kind === 'unavailable' &&
                    firewallState.reason === 'override-fragment' ? (
                      <p
                        className="mt-3 rounded-lg bg-card px-3 py-2 text-label text-paper-low"
                        data-testid="binding-firewall-override-fragment"
                      >
                        {t('upstream_accounts.egress.binding_firewall_override_fragment')}
                      </p>
                    ) : null}
                    {blacklistState.kind === 'editable' ? (
                      <div
                        className="mt-3 flex flex-col gap-2"
                        data-testid="binding-blacklist-editor"
                      >
                        <p className="m-0 text-label text-paper-mid">
                          {t('upstream_accounts.egress.binding_blacklist_scope', {
                            channel: selectedPlainName,
                          })}
                        </p>
                        <EgressAdmissionBlacklistFields
                          value={blacklistState.rules}
                          scope="binding"
                          disabled={setMut.isPending}
                          idPrefix="binding"
                          onChange={applyBlacklistRules}
                        />
                        <p className="m-0 text-label text-paper-low">
                          {t('upstream_accounts.egress.binding_blacklist_snapshot')}
                        </p>
                      </div>
                    ) : null}
                    {blacklistState.kind === 'diverged' ? (
                      <div
                        className="mt-3 flex flex-col gap-2 rounded-lg border border-warn/40 bg-warn-dim/40 p-3"
                        data-testid="binding-blacklist-diverged"
                      >
                        <span className="inline-flex items-center gap-2 text-label font-medium text-warn">
                          <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />
                          {t('upstream_accounts.egress.binding_blacklist_diverged_title')}
                        </span>
                        <p className="m-0 text-label text-paper-mid">
                          {t('upstream_accounts.egress.binding_blacklist_diverged_body')}
                        </p>
                        <p className="m-0 font-mono text-micro text-paper-low">
                          {armedBlacklistRules(blacklistState.rules).join(', ') ||
                            t('upstream_accounts.egress.binding_blacklist_none_armed')}
                        </p>
                      </div>
                    ) : null}
                    {blacklistState.kind === 'unavailable' &&
                    blacklistState.reason === 'profile-mode' ? (
                      <p
                        className="mt-3 rounded-lg bg-card px-3 py-2 text-label text-paper-low"
                        data-testid="binding-blacklist-unavailable"
                      >
                        {t('upstream_accounts.egress.binding_blacklist_profile_mode')}
                      </p>
                    ) : null}
                    {blacklistState.kind === 'unavailable' &&
                    blacklistState.reason === 'override-fragment' ? (
                      <p
                        className="mt-3 rounded-lg bg-card px-3 py-2 text-label text-paper-low"
                        data-testid="binding-blacklist-override-fragment"
                      >
                        {t('upstream_accounts.egress.binding_blacklist_override_fragment')}
                      </p>
                    ) : null}
                    {selectedBinding.override !== null ? (
                      <div
                        className="mt-3 flex flex-col gap-2 rounded-lg border border-warn/40 bg-warn-dim/40 p-3"
                        data-testid="binding-override-notice"
                      >
                        <span className="inline-flex items-center gap-2 text-label font-medium text-warn">
                          <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />
                          {t('upstream_accounts.egress.binding_override_title')}
                        </span>
                        <p className="m-0 text-label text-paper-mid">
                          {t('upstream_accounts.egress.binding_override_body')}
                        </p>
                        <pre className="m-0 max-h-40 overflow-auto rounded bg-card p-2 font-mono text-micro text-paper-mid">
                          {selectedBinding.override.json}
                        </pre>
                        {/*
                          Rendered from `selectedBinding.override`, not from
                          `blacklistState`, so it is here in every state that
                          has an override to clear — including `diverged`,
                          which is precisely when an operator needs it and the
                          state where the editor above is deliberately absent.
                        */}
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleClearOverride}
                            disabled={setMut.isPending}
                            className="hover:bg-bad-dim hover:text-bad"
                            data-testid="binding-override-clear"
                          >
                            <Eraser aria-hidden="true" className="h-3.5 w-3.5" />
                            <span>{t('upstream_accounts.egress.binding_override_clear')}</span>
                          </Button>
                          <span className="text-label text-paper-low">
                            {t('upstream_accounts.egress.binding_override_clear_hint')}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </section>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-line-soft bg-card px-6 py-4">
            <div className="flex min-h-5 flex-col gap-1 text-label">
              {formError !== null ? (
                <span className="font-medium text-bad" role="alert">{formError}</span>
              ) : dirty ? (
                <span className="inline-flex items-center gap-2 text-warn">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-[1.5px] bg-warn" />
                  {t('upstream_accounts.egress.unsaved_changes')}
                </span>
              ) : null}
              {unsupported.length > 0 ? (
                <span className="text-bad" data-testid="binding-override-unsupported-warning">
                  {t('upstream_accounts.egress.binding_override_unsupported', {
                    channels: unsupported.join(', '),
                  })}
                </span>
              ) : null}
              {editedAway.length > 0 ? (
                <span className="text-warn" data-testid="binding-override-save-warning">
                  {t('upstream_accounts.egress.binding_override_save_warning', {
                    channels: editedAway.join(', '),
                  })}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={onClose} disabled={setMut.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={setMut.isPending}
              >
                {setMut.isPending ? null : <Save aria-hidden="true" className="h-4 w-4" />}
                <span>{setMut.isPending ? t('common.saving') : t('common.save')}</span>
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {addOpen ? (
        <AddChannelBindingModal
          existingChannelIds={boundChannelIds}
          channels={upstreamChannels}
          profiles={profiles}
          preferredChannelId={initialAddChannelId}
          pending={setMut.isPending || channelsQ.isLoading}
          onAdd={addBinding}
          onClose={() => setAddOpen(false)}
        />
      ) : null}

      <Dialog
        open={saveConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !setMut.isPending) setSaveConfirm(null);
        }}
        size="md"
        closeLabel={t('common.close')}
        testId="binding-override-unsupported-confirm"
        title={
          <span className="inline-flex items-center gap-2">
            <TriangleAlert aria-hidden="true" className="h-5 w-5 text-bad" />
            {saveConfirm?.kind === 'clear'
              ? t('upstream_accounts.egress.binding_override_clear_title')
              : t('upstream_accounts.egress.binding_override_unsupported_title')}
          </span>
        }
        description={
          saveConfirm?.kind === 'clear'
            ? t('upstream_accounts.egress.binding_override_clear_body', {
              channel: saveConfirm.channel,
            })
            : t('upstream_accounts.egress.binding_override_unsupported', {
              channels: (saveConfirm?.kind === 'unsupported' ? saveConfirm.channels : []).join(', '),
            })
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setSaveConfirm(null)}
              disabled={setMut.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (saveConfirm !== null) submitConfirmedSave(saveConfirm);
              }}
              loading={setMut.isPending}
              data-testid="binding-override-unsupported-confirm-submit"
            >
              {saveConfirm?.kind === 'clear'
                ? t('upstream_accounts.egress.binding_override_clear_confirm')
                : t('upstream_accounts.egress.binding_override_unsupported_confirm')}
            </Button>
          </>
        }
      >
        {saveConfirm?.kind === 'clear' ? (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-label text-paper-mid">
              {t('upstream_accounts.egress.binding_override_clear_detail')}
            </p>
            {saveConfirm.alsoCleared.length > 0 ? (
              <p
                className="m-0 text-label text-bad"
                data-testid="binding-override-clear-also-cleared"
              >
                {t('upstream_accounts.egress.binding_override_unsupported', {
                  channels: saveConfirm.alsoCleared.join(', '),
                })}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="m-0 text-label text-paper-mid">
            {t('upstream_accounts.egress.binding_override_unsupported_detail')}
          </p>
        )}
      </Dialog>

      <CascadeConfirmDialog
        impact={cascadeConfirm?.impact ?? null}
        pending={setMut.isPending}
        onCancel={() => setCascadeConfirm(null)}
        onConfirm={handleCascadeConfirm}
      />
    </>
  );
}

function AddChannelBindingModal({
  existingChannelIds,
  channels,
  profiles,
  preferredChannelId,
  pending,
  onAdd,
  onClose,
}: {
  readonly existingChannelIds: ReadonlySet<string>;
  readonly channels: readonly UpstreamChannelRecord[];
  readonly profiles: readonly EgressProfileRecord[];
  readonly preferredChannelId: string | null;
  readonly pending: boolean;
  readonly onAdd: (channel: { readonly channel_id: string; readonly channel_slug: string; readonly egress_profile_id: string | null }) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const availableChannels = useMemo(
    () => channels.filter((channel) => !existingChannelIds.has(channel.channel_id)),
    [channels, existingChannelIds],
  );
  const [channelId, setChannelId] = useState(
    () => availableChannels.find((channel) => channel.channel_id === preferredChannelId)?.channel_id ??
      availableChannels[0]?.channel_id ??
      '',
  );
  const [profileId, setProfileId] = useState('');
  const selectedChannel = availableChannels.find((channel) => channel.channel_id === channelId);
  const channelRequired = selectedChannel === undefined;

  useEffect(() => {
    setChannelId((current) => {
      if (preferredChannelId !== null && availableChannels.some((channel) => channel.channel_id === preferredChannelId)) {
        return preferredChannelId;
      }
      if (availableChannels.some((channel) => channel.channel_id === current)) {
        return current;
      }
      return availableChannels[0]?.channel_id ?? '';
    });
  }, [availableChannels, preferredChannelId]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (channelRequired || selectedChannel === undefined) {
      return;
    }
    onAdd({
      channel_id: selectedChannel.channel_id,
      channel_slug: selectedChannel.channel_slug,
      egress_profile_id: profileId.length === 0 ? null : profileId,
    });
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!pending) onClose();
      }}
      titleId="add-channel-binding-title"
      testId="add-channel-binding-modal"
    >
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit}>
        <div className="flex items-center gap-2">
          <Link2 aria-hidden="true" className="h-5 w-5 text-signal" />
          <h2 id="add-channel-binding-title" className="m-0 text-title text-paper">
            {t('upstream_accounts.egress.add_modal_title')}
          </h2>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.egress.add_modal_channel_id')}
          </span>
          <Select
            value={channelId}
            onValueChange={setChannelId}
            disabled={pending || availableChannels.length === 0}
            ariaLabel={t('upstream_accounts.egress.add_modal_channel_id')}
            placeholder={t('upstream_accounts.egress.no_available_channels')}
            mono
            options={availableChannels.map((channel) => ({
              value: channel.channel_id,
              label: `${channel.channel_slug} · ${channel.channel_id}`,
            }))}
          />
          <span className="text-label text-paper-low">
            {t('upstream_accounts.egress.add_modal_channel_hint')}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_accounts.egress.add_modal_profile')}
          </span>
          <Select
            value={profileId.length > 0 ? profileId : DEFAULT_PROFILE_SENTINEL}
            onValueChange={(value) => setProfileId(value === DEFAULT_PROFILE_SENTINEL ? '' : value)}
            disabled={pending}
            ariaLabel={t('upstream_accounts.egress.add_modal_profile')}
            options={[
              { value: DEFAULT_PROFILE_SENTINEL, label: t('upstream_accounts.egress.default') },
              ...profiles.map((profile) => ({
                value: profile.id,
                label: `${profile.name} · ${egressConfigSummaryLabel(profile.egress_config, t)}`,
              })),
            ]}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending || channelRequired}>
            {t('upstream_accounts.egress.add_modal_submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Cascade confirmation ────────────────────────────────────────────────

/**
 * 409 CASCADE_CONFIRMATION_REQUIRED review dialog. Groups the structured
 * impact returned by the gateway — SKU routing targets to be deleted and
 * capabilities flipped to mismatch —
 * so the operator sees the blast radius before re-submitting with
 * `confirm_cascade: true`.
 */
function CascadeConfirmDialog({
  impact,
  pending,
  onCancel,
  onConfirm,
}: {
  readonly impact: KeyChannelBindingCascadeImpact | null;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <Dialog
      open={impact !== null}
      onOpenChange={(open) => {
        if (!open && !pending) {
          onCancel();
        }
      }}
      size="lg"
      closeLabel={t('common.close')}
      testId="key-channel-cascade-confirm"
      title={
        <span className="inline-flex items-center gap-2">
          <TriangleAlert aria-hidden="true" className="h-5 w-5 text-warn" />
          {t('upstream_accounts.egress.cascade.title')}
        </span>
      }
      description={t('upstream_accounts.egress.cascade.body')}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={pending}
            data-testid="key-channel-cascade-confirm-submit"
          >
            {t('upstream_accounts.egress.cascade.confirm')}
          </Button>
        </>
      }
    >
      {impact === null ? null : (
        <div className="flex flex-col gap-4">
          <CascadeImpactGroup
            title={t('upstream_accounts.egress.cascade.sku_targets', {
              count: impact.sku_targets_deleted.length,
            })}
            items={impact.sku_targets_deleted.map((row) => ({
              key: row.id,
              label: t('upstream_accounts.egress.cascade.sku_target_item', {
                scope: row.scope,
                sku: row.sku_name,
                model: row.model,
                capability: row.capability_id ?? '—',
              }),
              detail: row.upstream_channel_slug ?? row.upstream_channel_id,
            }))}
          />
          <CascadeImpactGroup
            title={t('upstream_accounts.egress.cascade.capabilities', {
              count: impact.capabilities_marked_mismatch.length,
            })}
            items={impact.capabilities_marked_mismatch.map((row) => ({
              key: row.id,
              label: t('upstream_accounts.egress.cascade.capability_item', {
                channel: row.channel_slug,
                model: row.model,
              }),
              detail: row.protocol,
            }))}
          />
        </div>
      )}
    </Dialog>
  );
}

function CascadeImpactGroup({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly { readonly key: string; readonly label: string; readonly detail: string }[];
}): JSX.Element | null {
  if (items.length === 0) {
    return null;
  }
  return (
    <section>
      <h3 className="m-0 text-label font-medium text-paper-mid">{title}</h3>
      <ul className="mt-2 flex list-none flex-col gap-1 p-0">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-center justify-between gap-3 rounded bg-well px-3 py-2"
          >
            <span className="min-w-0 truncate text-dense text-paper">{item.label}</span>
            <span className="shrink-0 font-mono text-micro text-paper-low">{item.detail}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
